import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, UserPlus, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import AuthFooter from "@/components/AuthFooter";
import { downloadWelcomePdf } from "@/lib/welcomePdf";

export default function SignupPage() {
  const [sp] = useSearchParams();
  const refFromUrl = sp.get("ref") || "";
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirm_password: "",
    sponsor_user_code: refFromUrl,
  });
  const [showPw, setShowPw] = useState(false);
  const [showCpw, setShowCpw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Sponsor lookup: null = idle, "loading", {name} on success, or "invalid"
  const [sponsorState, setSponsorState] = useState(null);
  const lookupTimer = useRef(null);
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (refFromUrl) setForm((f) => ({ ...f, sponsor_user_code: refFromUrl }));
  }, [refFromUrl]);

  // Debounced sponsor lookup whenever sponsor_user_code changes
  useEffect(() => {
    const code = form.sponsor_user_code.trim().toUpperCase();
    if (lookupTimer.current) clearTimeout(lookupTimer.current);
    if (code.length < 3) {
      setSponsorState(null);
      return;
    }
    setSponsorState("loading");
    lookupTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/auth/sponsor/${encodeURIComponent(code)}`);
        setSponsorState({ name: data.name });
      } catch {
        setSponsorState("invalid");
      }
    }, 400);
    return () => {
      if (lookupTimer.current) clearTimeout(lookupTimer.current);
    };
  }, [form.sponsor_user_code]);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm_password) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        password: form.password,
        sponsor_user_code: form.sponsor_user_code.trim().toUpperCase(),
      };
      if (form.email.trim()) payload.email = form.email.trim();
      if (form.phone.trim()) payload.phone = form.phone.trim();
      const user = await register(payload);
      toast.success(`Welcome, ${user.name}! Your ID is ${user.user_code}`);
      try {
        await downloadWelcomePdf({
          name: user.name,
          user_code: user.user_code,
          password: form.password,
        });
      } catch (pdfErr) {
        console.error("Welcome PDF failed", pdfErr);
      }
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col"
         style={{
           background:
             "radial-gradient(circle at 20% 20%, #ef4444 0%, #dc2626 45%, #b91c1c 100%)",
         }}
    >
      <div className="flex-1 flex items-center justify-center px-4 py-10">
      <div
        data-testid="signup-card"
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 sm:p-10 relative z-10"
      >
        <div className="flex items-center justify-center gap-3 mb-1">
          <img
            src="https://customer-assets-jt897jd0.emergentagent.net/job_ref-connect-app/artifacts/02ztij1f_logo.png"
            alt="Unique Deal India"
            className="h-14 w-14 object-contain"
          />
          <h1 className="text-2xl font-extrabold text-red-700 tracking-wide"
              style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
            UNIQUE DEAL INDIA
          </h1>
        </div>
        <p className="text-center text-slate-600 mb-6 text-sm">
          Create your account
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Full Name *</label>
            <input
              data-testid="signup-name-input"
              value={form.name}
              onChange={change("name")}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-red-500 outline-none"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Sponsor ID *</label>
            <input
              data-testid="signup-sponsor-input"
              value={form.sponsor_user_code}
              onChange={change("sponsor_user_code")}
              placeholder="UNE26000001"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-red-500 outline-none uppercase"
              required
            />
            {sponsorState === "loading" && (
              <div
                data-testid="sponsor-lookup-loading"
                className="mt-2 flex items-center gap-2 text-xs text-slate-500"
              >
                <Loader2 size={14} className="animate-spin" /> Checking sponsor…
              </div>
            )}
            {sponsorState && sponsorState.name && (
              <div
                data-testid="sponsor-name-preview"
                className="mt-2 flex items-center gap-2 text-sm font-semibold text-emerald-600"
              >
                <CheckCircle2 size={16} /> {sponsorState.name}
              </div>
            )}
            {sponsorState === "invalid" && (
              <div
                data-testid="sponsor-invalid"
                className="mt-2 flex items-center gap-2 text-sm font-semibold text-red-600"
              >
                <XCircle size={16} /> Invalid Sponsor ID
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <input
              data-testid="signup-email-input"
              type="email"
              value={form.email}
              onChange={change("email")}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-red-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
            <input
              data-testid="signup-phone-input"
              value={form.phone}
              onChange={change("phone")}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-red-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Password *</label>
            <div className="relative">
              <input
                data-testid="signup-password-input"
                type={showPw ? "text" : "password"}
                value={form.password}
                onChange={change("password")}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 pr-12 focus:ring-2 focus:ring-red-500 outline-none"
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
                data-testid="signup-toggle-password"
              >
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-1">At least 6 characters</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Confirm Password *</label>
            <div className="relative">
              <input
                data-testid="signup-confirm-password-input"
                type={showCpw ? "text" : "password"}
                value={form.confirm_password}
                onChange={change("confirm_password")}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 pr-12 focus:ring-2 focus:ring-red-500 outline-none"
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowCpw((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
                data-testid="signup-toggle-confirm-password"
              >
                {showCpw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {form.confirm_password && form.password !== form.confirm_password && (
              <p
                data-testid="password-mismatch"
                className="text-xs text-red-600 mt-1 flex items-center gap-1"
              >
                <XCircle size={12} /> Passwords do not match
              </p>
            )}
          </div>

          {error && (
            <div data-testid="signup-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <Button
            data-testid="signup-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white font-semibold rounded-lg py-6 shadow-md transition-transform active:scale-[0.98]"
          >
            {loading ? "Creating..." : (
              <span className="flex items-center gap-2"><UserPlus size={18} /> Create Account</span>
            )}
          </Button>
        </form>

        <div className="mt-6 text-center text-sm text-slate-600">
          Already have an account?{" "}
          <Link data-testid="login-link" to="/login" className="text-purple-700 font-semibold hover:underline">
            Sign In
          </Link>
        </div>
      </div>
      </div>
      <AuthFooter />
    </div>
  );
}
